import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CmsService, TranslationService } from '@spartacus/core';
import { I18nModule } from '@spartacus/core';
import { SpinnerModule } from '@spartacus/storefront';
import { Observable, map, take } from 'rxjs';
import { KnBrCommonService } from '../../../services/kn-br-common.service';

interface PriceListReportRow {
  line: number | null;
  reportId: string;
  atgDivision: string;
  soldToPartner: string;
  soldToName: string;
  soldToLocation: string;
  createDateTime: string;
  reportStatus: string;
}

@Component({
  selector: 'knbr-atg-price-list-report',
  imports: [CommonModule, I18nModule, SpinnerModule],
  templateUrl: './atg-price-list-report.component.html',
  styleUrl: './atg-price-list-report.component.scss',
  standalone: true,
})
export class AtgPriceListReportComponent implements OnInit {
  isPriceListAllowed = false;
  reportRows: PriceListReportRow[] = [];
  isReportLoading = false;
  hasReportFetchAttempted = false;
  hasReportError = false;
  sortField: 'createDateTime' | 'reportStatus' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';
  pageTitle$: Observable<string> = this.cmsService.getCurrentPage().pipe(map((page) => page?.title));
  private notAvailableValue = 'N/A';

  constructor(
    private knBrCommonService: KnBrCommonService,
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef,
    private cmsService: CmsService
  ) {}

  ngOnInit(): void {
    this.isPriceListAllowed = true;
    this.initializeReport();
  }

  private initializeReport(): void {
    this.translationService
      .translate('atgPriceList.notAvailable')
      .pipe(take(1))
      .subscribe({
        next: (value) => {
          if (typeof value === 'string' && value.trim().length > 0) {
            this.notAvailableValue = value;
          }
          this.fetchPriceListReport();
        },
        error: () => {
          this.fetchPriceListReport();
        },
      });
  }

  fetchPriceListReport(): void {
    this.isReportLoading = true;
    this.hasReportFetchAttempted = true;
    this.hasReportError = false;
    this.reportRows = [];

    this.knBrCommonService.sendMyAccountPriceList().pipe(take(1)).subscribe(
      (response: any) => {
        const reports = this.extractReports(response);
        this.reportRows = reports.map((report: any) => ({
          line: report?.line ?? null,
          reportId: report?.reportId || this.notAvailableValue,
          atgDivision: report?.atgDivision || this.notAvailableValue,
          soldToPartner: report?.soldToPartner || this.notAvailableValue,
          soldToName: report?.soldToName || this.notAvailableValue,
          soldToLocation: report?.soldToLocation || this.notAvailableValue,
          createDateTime: report?.createDateTime || this.notAvailableValue,
          reportStatus: report?.reportStatus || this.notAvailableValue,
        }));
        this.isReportLoading = false;
        this.cdr.detectChanges();
      },
      () => {
        this.hasReportError = true;
        this.reportRows = [];
        this.isReportLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  onSortColumn(field: 'createDateTime' | 'reportStatus'): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortField) {
      return;
    }

    const sortField = this.sortField;

    this.reportRows.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      let comparison = 0;
      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  private extractReports(response: any): any[] {
    const queue: any[] = [response];

    while (queue.length > 0) {
      let current = queue.shift();

      if (current == null) {
        continue;
      }

      if (typeof current === 'string') {
        try {
          current = JSON.parse(current);
        } catch {
          continue;
        }
      }

      if (Array.isArray(current)) {
        if (current.length > 0 && this.isReportLike(current[0])) {
          return current;
        }

        current.forEach((item) => {
          if (item && (typeof item === 'object' || typeof item === 'string')) {
            queue.push(item);
          }
        });
        continue;
      }

      if (typeof current !== 'object') {
        continue;
      }

      const reportsValue = (current as any).reports ?? (current as any).Reports;
      const reportValue = (current as any).report ?? (current as any).Report;

      if (Array.isArray(reportsValue) && reportsValue.length > 0 && this.isReportLike(reportsValue[0])) {
        return reportsValue;
      }

      if (Array.isArray(reportValue) && reportValue.length > 0 && this.isReportLike(reportValue[0])) {
        return reportValue;
      }

      if (reportsValue && typeof reportsValue === 'object') {
        if (Array.isArray((reportsValue as any).report) && (reportsValue as any).report.length > 0) {
          return (reportsValue as any).report;
        }
        if (this.isReportLike(reportsValue)) {
          return [reportsValue];
        }
      }

      if (reportValue && typeof reportValue === 'object' && this.isReportLike(reportValue)) {
        return [reportValue];
      }

      Object.values(current).forEach((value) => {
        if (value && (typeof value === 'object' || typeof value === 'string')) {
          queue.push(value);
        }
      });
    }

    return [];
  }

  private isReportLike(value: any): boolean {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return (
      'reportId' in value ||
      'atgDivision' in value ||
      'soldToPartner' in value ||
      'reportStatus' in value ||
      'createDateTime' in value
    );
  }
}
